import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { JourneyApi } from "@/api/client";
import { AI_FALLBACK_NOTICE, DEFAULT_DEMO_QUESTION } from "@/lib/constants";
import { createTicketCode, TICKET_TEMPLATE_NO, todayLabel } from "@/lib/ids";
import { actionKeywordFromText, cleanUserText } from "@/lib/text";
import { TICKET_ARTWORK, TICKET_GEOMETRY } from "@/lib/ticketLayout";
import { downloadTicketPng } from "@/lib/ticket";
import type { ActionResult, JiaxingCase, KnowledgeCard, QuestionAnalysis, QuestionCard, ReflectionResult, TicketData, Topic } from "@/types/journey";
import { AudioManager } from "./audio/AudioManager";
import { CursorGlow } from "./environment/CursorGlow";
import { EnvironmentOverlay } from "./environment/EnvironmentOverlay";
import { LivingWater } from "./environment/LivingWater";
import { SceneBackground } from "./environment/SceneBackground";
import { useTicketArtwork } from "./hooks/useTicketArtwork";
import { useViewportProfile } from "./hooks/useViewportProfile";
import { WaterInput } from "./interactions/WaterInput";
import { sceneLabel, sceneOrder, type V2Scene } from "./types";
import "./styles/tokens.css";
import "./styles/experience.css";
import "./styles/cinema.css";
import "./styles/flash.css";

type CssVars = CSSProperties & { [key: `--${string}`]: string | number };

/** 幕间留白：让"水面正在读"的仪式感不随本地 mock 瞬时消失。 */
function pause(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** 标题行遮罩：文字从一行"水面下"整行升起，像字幕与碑文。 */
function MaskLine({ at, children }: { at: number; children: ReactNode }) {
  return (
    <span className="v2-mline">
      <span className="v2-mline-in" style={{ "--d": `${at}s` } as CssVars}>{children}</span>
    </span>
  );
}

const QUESTION_MIN_BEAT = 1250;
const REFLECTION_MIN_BEAT = 2300;

/** 逐字浮现：AI 生成的文字像被一笔一笔写在水面上。 */
function Typewriter({ text, speed = 26, startDelay = 0, className = "", onDone }: {
  text: string; speed?: number; startDelay?: number; className?: string; onDone?: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [count, setCount] = useState(() => (reduceMotion ? text.length : 0));

  useEffect(() => {
    if (reduceMotion) { setCount(text.length); return; }
    setCount(0);
    let index = 0;
    let timer = 0;
    const tick = () => {
      index += 1;
      setCount(index);
      if (index < text.length) timer = window.setTimeout(tick, speed);
      else onDone?.();
    };
    const starter = window.setTimeout(tick, startDelay);
    return () => { window.clearTimeout(starter); window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, speed, startDelay, reduceMotion]);

  const complete = count >= text.length;
  return (
    <span className={"v2-type" + (complete ? " is-complete" : "") + (className ? " " + className : "")}>
      {text.slice(0, count)}
      <i className="v2-caret" aria-hidden="true" />
    </span>
  );
}

const BRAND_TITLE = "红船三问";
const CHAPTER_LABELS = ["首创", "奋斗", "奉献"] as const;

/** 终幕升腾的灯火：位置/大小/节奏都是稳定的伪随机，避免重渲染抖动。 */
const EMBER_SPECS: CssVars[] = Array.from({ length: 30 }, (_, index) => ({
  "--l": `${3 + ((index * 37 + 11) % 94)}%`,
  "--w": `${index % 5 === 0 ? 6 : index % 2 === 0 ? 4 : 3}px`,
  "--d": `${8.5 + ((index * 7) % 6)}s`,
  "--dd": `-${((index * 2.7) % 9).toFixed(1)}s`,
  "--s": `${(index % 2 === 0 ? 1 : -1) * (16 + ((index * 13) % 54))}px`,
}));

/** 三问进度：当前问、已完成问、未开始问。 */
function chapterMarks(scene: V2Scene): Array<"todo" | "current" | "done"> {
  const index = sceneOrder.indexOf(scene);
  if (index >= 5) return ["done", "done", "done"];
  return CHAPTER_LABELS.map((_, chapter) => {
    if (chapter === index - 2) return "current";
    if (chapter < index - 2) return "done";
    return "todo";
  });
}

/** 主题 → 精神坐标（仅用于把哪张固定理论卡放在第一位，AI 不写卡内文字）。 */
const THEME_KEYS: Array<KnowledgeCard["theme"]> = ["first", "struggle", "dedication"];
const THEME_LABELS: Record<KnowledgeCard["theme"], string> = { first: "首创", struggle: "奋斗", dedication: "奉献" };

function spiritThemeOf(topic: Topic | null | undefined): KnowledgeCard["theme"] {
  switch (topic) {
    case "innovation":
    case "collaboration":
    case "uncertainty":
      return "first";
    case "persistence":
    case "growth":
      return "struggle";
    case "responsibility":
    case "service":
      return "dedication";
    default:
      return "first";
  }
}

/** 固定释义按句群均衡分成至多三段，保持阅读节奏。 */
function theoryParagraphs(value: string): string[] {
  const sentences = value.match(/[^。！？]+[。！？]?/g)?.map((item) => item.trim()).filter(Boolean) ?? [];
  if (sentences.length <= 3) return sentences.length ? sentences : [value];
  const groups = ["", "", ""];
  sentences.forEach((sentence, index) => { groups[index % 3] += sentence; });
  return groups.filter(Boolean);
}

const FIRST_CHOICES = {
  A: { title: "沿着熟悉的方向", detail: "再观察一段时间" },
  B: { title: "离开已有航线", detail: "先做一次小范围尝试" },
} as const;
const STRUGGLE_CHOICES = {
  A: { title: "先停下来", detail: "等看清方向再走" },
  B: { title: "继续向前", detail: "找出问题，再调整一次" },
} as const;
const QUESTION_THOUGHTS = ["我该坚持现在的方向吗？", "如果看不到结果，还要继续吗？", "怎样迈出真正的第一步？"];
const DEDICATION_THOUGHTS = ["我愿意继续", "我会犹豫", "我还不知道"];

const DEV_CASE: JiaxingCase = {
  id: "v2-local-visual-fixture", theme: "innovation", category: "开发演示", region: "嘉兴",
  title: "一件事，先从现场的一小步开始",
  summary: "他们面对的是一个没有现成路径、也不能只靠想象解决的问题。",
  content: "他们先走进现场，把目标拆成一次可以验证的小尝试。变化并非立刻发生，但真实的反馈让下一步逐渐清晰。",
  image_url: null, source_name: "本地视觉联调 fixture · 不进入公开环境", source_url: "", source_date: "",
  review_status: "demo", environment: "staging", reviewed_at: null, sort_order: 1,
};

function splitReflection(value: string): string[] {
  const sentences = value.match(/[^。！？]+[。！？]?/g)?.map((item) => item.trim()).filter(Boolean) ?? [value];
  if (sentences.length <= 3) return sentences;
  const groups = ["", "", ""];
  sentences.forEach((sentence, index) => { groups[index % 3] += sentence; });
  return groups.filter(Boolean);
}

function storyBeats(item: JiaxingCase): [string, string, string] {
  const parts = item.content.match(/[^。！？]+[。！？]?/g)?.map((value) => value.trim()).filter(Boolean) ?? [];
  return [item.summary, parts[0] ?? "他们把问题带回现场，从一次可以验证的小行动开始。", parts.slice(1).join("") || "真实的反馈，让下一步逐渐清晰。"];
}

function initialScene(): V2Scene {
  const value = new URLSearchParams(window.location.search).get("scene") as V2Scene | null;
  return value && sceneOrder.includes(value) ? value : "intro";
}

/** 每条路线一条"语义水痕"：形状按文字含义设计。 */
const ROUTE_MARKS: Record<string, string> = {
  /* 沿着熟悉的方向 → 在熟悉的水域绕一个回环（起点线接入环） */
  loop: "M6 41 L 16 31 M35 22 a 11 11 0 1 1 -22 0 a 11 11 0 1 1 22 0",
  /* 离开已有航线 → 一道向远方斜切出去的弧线 */
  depart: "M8 38 C 24 34 34 22 42 8 M8 38 l 5 -3",
  /* 先停下来 → 一条平直的线，尽头一个锚点 */
  halt: "M8 22 H 36 M39.4 22 a 3.4 3.4 0 1 1 -6.8 0 a 3.4 3.4 0 1 1 6.8 0",
  /* 继续向前调整 → 一浪接一浪地前进 */
  surge: "M8 26 q 10 -18 22 0 t 22 0 t 22 0",
};

function RouteText({ title, detail, direction, mark, selected, disabled, onClick }: {
  title: string; detail: string; direction: "left" | "right"; mark: keyof typeof ROUTE_MARKS;
  selected: boolean; disabled: boolean; onClick: () => void;
}) {
  return (
    <button className={"v2-route-text is-" + direction + (selected ? " is-selected" : "")} type="button" disabled={disabled} onClick={onClick}>
      <svg className="v2-route-mark" viewBox="0 0 52 44" aria-hidden="true">
        <path className="v2-rm-base" d={ROUTE_MARKS[mark]} />
        <path className="v2-rm-glow" d={ROUTE_MARKS[mark]} />
      </svg>
      <span className="v2-route-copy">
        <span className="v2-route-depth" aria-hidden="true">{direction === "right" ? "远处水路" : "近处岸线"}</span>
        <span className="v2-route-line" aria-hidden="true"><i /></span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
    </button>
  );
}

/**
 * 票面：把空白票版"印"成一张完整的票。
 *
 * 三个关键约束：
 *
 * 1. 它必须渲染在 .v2-bg-current 里（经 SceneBackground 的 children），因为背景素材
 *    带着两层动画——.v2-bg-current 的 50s 漂移（scale 最高 1.06）与图片自身的缓推
 *    （1.01→1.045）。叠加层若在外面，位置会随进度漂掉约 10%。
 *
 * 2. 几何与字号全部来自 src/lib/ticketLayout.ts——那是屏幕与 Canvas 导出共用的
 *    唯一来源；这里只负责把它摊成 CSS 变量。此前屏幕一套 CSS、导出一套硬编码坐标，
 *    结果"看到的"和"下载到的"是两张完全不同的票。
 *
 * 3. 用哪一版票版由 useTicketArtwork 决定（竖屏窗口切竖版），**必须与
 *    SceneBackground 拿到的 ticketArt 是同一个值**——票面是印在素材上的，
 *    素材换了而几何没换，字就会印到票外。
 */
function TicketFace({ ticket, artKey }: { ticket: TicketData; artKey: "mobile" | "desktop" }) {
  const geometry = TICKET_GEOMETRY[artKey];
  const art = TICKET_ARTWORK[artKey];
  const percent = (value: number) => `${value * 100}%`;
  const cqw = (ratio: number) => `${ratio * 100}cqw`;
  const vars = {
    /* 票版盒子的比例也走变量：CSS 里的默认值是横版，叠加层必须复刻"实际渲染的那张素材" */
    "--ticket-r": String(art.ratio),
    "--ticket-ratio": `${art.width} / ${art.height}`,
    "--tk-fields-left": percent(geometry.fields.left),
    "--tk-fields-top": percent(geometry.fields.top),
    "--tk-fields-width": percent(geometry.fields.width),
    "--tk-fields-rotate": `${geometry.fieldsRotate}deg`,
    "--tk-seal-left": percent(geometry.seal.left),
    "--tk-seal-top": percent(geometry.seal.top),
    "--tk-seal-width": percent(geometry.seal.width),
    "--tk-seal-height": percent(geometry.seal.height),
    "--tk-seal-rotate": `${geometry.sealRotate}deg`,
    "--tk-align": geometry.align,
    /* 居中时字距会把整体推左，用等量缩进补回；左对齐则不需要（此前这条跟着
       max-width:699px 的媒体查询走，切了票版就与对齐方式不匹配了） */
    "--tk-indent-head": geometry.align === "center" ? ".1em" : "0",
    "--tk-indent-label": geometry.align === "center" ? ".26em" : "0",
    "--tk-font-head": cqw(geometry.font.head),
    "--tk-font-label": cqw(geometry.font.label),
    "--tk-font-answer": cqw(geometry.font.answer),
    "--tk-font-no": cqw(geometry.font.no),
    "--tk-font-seal": cqw(geometry.font.seal),
    "--tk-gap-label": cqw(geometry.gap.label),
    "--tk-gap-answer": cqw(geometry.gap.answer),
    "--tk-gap-no": cqw(geometry.gap.no),
    "--tk-answer-lines": String(geometry.answerLines),
    "--tk-answer-lh": String(geometry.answerLineHeight),
  } as CssVars;
  return (
    <div className="v2-ticket-plane">
      <div className="v2-ticket-face" style={vars}>
        <div className="v2-ticket-fields">
          <p className="v2-ticket-head">红船青年船票</p>
          <p className="v2-ticket-label">我的答案</p>
          <div className="v2-ticket-dynamic"><strong>{ticket.action}</strong></div>
          <p className="v2-ticket-no">No. {ticket.ticketCode}</p>
        </div>
        <span className="v2-ticket-seal">已启航</span>
      </div>
    </div>
  );
}

export function ExperienceV2() {
  const api = useMemo(() => new JourneyApi(), []);
  const audio = useMemo(() => new AudioManager(), []);
  const profile = useViewportProfile();
  const ticketArt = useTicketArtwork();
  const reduceMotion = useReducedMotion();
  const isDemo = new URLSearchParams(window.location.search).get("demo") === "1";
  const allowFixture = isDemo || ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const [scene, setScene] = useState<V2Scene>(initialScene);
  const [previousScene, setPreviousScene] = useState<V2Scene | null>(null);
  const [transitionDir, setTransitionDir] = useState<1 | -1>(1);
  const [sessionId, setSessionId] = useState("");
  const [question, setQuestion] = useState(isDemo ? DEFAULT_DEMO_QUESTION : "");
  const [analysis, setAnalysis] = useState<QuestionAnalysis | null>(null);
  const [questionTwo, setQuestionTwo] = useState<QuestionCard | null>(null);
  const [firstChoice, setFirstChoice] = useState<"A" | "B" | null>(null);
  const [secondChoice, setSecondChoice] = useState<"A" | "B" | null>(null);
  const [dedication, setDedication] = useState("");
  const [reflection, setReflection] = useState<ReflectionResult | null>(null);
  const [theoryCards, setTheoryCards] = useState<KnowledgeCard[]>([]);
  const [theoryPending, setTheoryPending] = useState(false);
  const [theoryChoice, setTheoryChoice] = useState<KnowledgeCard["theme"] | null>(null);
  const theoryRequested = useRef(false);
  const [jiaxingCase, setJiaxingCase] = useState<JiaxingCase | null>(null);
  const [caseLoaded, setCaseLoaded] = useState(false);
  const [action, setAction] = useState("");
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [completed, setCompleted] = useState<number | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showQuestionThoughts, setShowQuestionThoughts] = useState(false);
  const [showDedicationThoughts, setShowDedicationThoughts] = useState(false);
  const [selectedRecall, setSelectedRecall] = useState<"first" | "struggle" | "dedication" | null>(null);
  const [reflectedCount, setReflectedCount] = useState(0);
  const [mobileCaseBeat, setMobileCaseBeat] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const transitionTimer = useRef<number | null>(null);
  const rippleTimer = useRef<number | null>(null);

  /** 输入时在纸船旁泛起一圈小涟漪（节流，避免每键一次）。 */
  function typingRipple() {
    if (rippleTimer.current !== null) return;
    rippleTimer.current = window.setTimeout(() => { rippleTimer.current = null; }, 140);
    window.dispatchEvent(new CustomEvent("v2:boat-ripple", { detail: { n: 1 } }));
  }

  useEffect(() => {
    let active = true;
    void api.startSession().then((value) => { if (active) setSessionId(value); });
    return () => { active = false; audio.dispose(); };
  }, [api, audio]);

  useEffect(() => {
    audio.setScene(scene);
    const timer = window.setTimeout(() => mainRef.current?.querySelector<HTMLElement>(".v2-scene-layer h1")?.focus({ preventScroll: true }), reduceMotion ? 220 : 920);
    return () => window.clearTimeout(timer);
  }, [audio, reduceMotion, scene]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewportHeight = () => {
      mainRef.current?.style.setProperty("--v2-viewport-height", `${viewport?.height ?? window.innerHeight}px`);
    };
    updateViewportHeight();
    viewport?.addEventListener("resize", updateViewportHeight, { passive: true });
    window.addEventListener("orientationchange", updateViewportHeight, { passive: true });
    return () => {
      viewport?.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (scene !== "dedication") return;
    setShowDedicationThoughts(false);
    const timer = window.setTimeout(() => setShowDedicationThoughts(true), reduceMotion ? 120 : 2400);
    return () => window.clearTimeout(timer);
  }, [reduceMotion, scene]);

  /* 新的反思到来时，从头开始逐字浮现 */
  useEffect(() => { setReflectedCount(0); }, [reflection]);

  useEffect(() => {
    if (scene !== "theory" || theoryRequested.current) return;
    theoryRequested.current = true;
    setTheoryPending(true);
    void api.publishedKnowledge()
      .then((cards) => setTheoryCards(Array.isArray(cards) ? cards : []))
      .finally(() => setTheoryPending(false));
  }, [api, scene]);

  useEffect(() => {
    if (scene !== "ticket") return;
    const timer = window.setTimeout(() => audio.playSeal(), reduceMotion ? 120 : 3050);
    return () => window.clearTimeout(timer);
  }, [audio, reduceMotion, scene]);

  /* 灯火幕要给出"已有多少位青年留下答案"。正常旅程里 collectTicket 的 complete()
     已经带回总数；直达本幕（?scene=lights / 截图）时再补拉一次公开统计。 */
  useEffect(() => {
    if (scene !== "lights" || completed !== null) return;
    let active = true;
    void api.publicStats().then((stats) => { if (active) setCompleted(stats.completed); });
    return () => { active = false; };
  }, [api, completed, scene]);

  function showMessage(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 3600);
  }

  function go(next: V2Scene, delay = 0) {
    audio.whoosh();
    setTransitionDir(sceneOrder.indexOf(next) >= sceneOrder.indexOf(scene) ? 1 : -1);
    setPulse(true);
    window.setTimeout(() => {
      setPreviousScene(scene);
      setScene(next);
      if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
      transitionTimer.current = window.setTimeout(() => { setPreviousScene(null); setPulse(false); }, reduceMotion ? 260 : 1350);
    }, reduceMotion ? Math.min(delay, 80) : delay);
  }

  function reset() {
    setQuestion(isDemo ? DEFAULT_DEMO_QUESTION : "");
    setAnalysis(null); setQuestionTwo(null); setFirstChoice(null); setSecondChoice(null);
    setDedication(""); setReflection(null); setJiaxingCase(null); setCaseLoaded(false);
    setTheoryCards([]); setTheoryChoice(null); theoryRequested.current = false;
    setAction(""); setActionResult(null); setTicket(null); setCompleted(null);
    setShowQuestionThoughts(false); setSelectedRecall(null); setMobileCaseBeat(-1); setReflectedCount(0); go("intro");
  }

  async function submitQuestion() {
    const safe = cleanUserText(question);
    if (safe.length < 5 || safe.length > 20) { showMessage("把问题写在 5 到 20 个字之间，再交给红船。"); return; }
    setBusy(true); setPulse(true); audio.tap();
    const [result] = await Promise.all([api.analyzeQuestion(sessionId, safe), pause(QUESTION_MIN_BEAT)]);
    setQuestion(safe); setAnalysis(result.value); setBusy(false); go("first", 420);
    if (result.source !== "api") showMessage(AI_FALLBACK_NOTICE);
  }

  async function chooseFirst(value: "A" | "B") {
    if (!analysis || firstChoice || busy) return;
    setFirstChoice(value); setPulse(true); setBusy(true); audio.tap();
    const result = await api.nextQuestion(sessionId, analysis.topic, value);
    setQuestionTwo(result.value.questionTwo); setBusy(false); go("struggle", 620);
    if (result.source !== "api") showMessage(AI_FALLBACK_NOTICE);
  }

  function chooseStruggle(value: "A" | "B") {
    if (secondChoice) return;
    setSecondChoice(value); setPulse(true); audio.tap(); go("dedication", 800);
  }

  function submitDedication() {
    const safe = cleanUserText(dedication);
    if (!safe) { showMessage("留下一句真实的话，哪怕只是“我会犹豫”。"); return; }
    setDedication(safe); setBusy(true); setPulse(true); audio.tap();
    const firstAnswer = firstChoice ? FIRST_CHOICES[firstChoice].title + "，" + FIRST_CHOICES[firstChoice].detail : "";
    const secondAnswer = secondChoice ? STRUGGLE_CHOICES[secondChoice].title + "，" + STRUGGLE_CHOICES[secondChoice].detail : "";
    const request = api.reflection({ sessionId, originalQuestion: question, firstAnswer, secondAnswer, thirdAnswer: safe })
      .then((result) => { setReflection(result.value); if (result.source !== "api") showMessage(AI_FALLBACK_NOTICE); });
    void Promise.all([request, pause(REFLECTION_MIN_BEAT)]).finally(() => setBusy(false));
    go("reflection", 1450);
  }

  async function continueToTheory() {
    if (!reflection || busy) return;
    go("theory", 360);
  }

  async function continueToJiaxing() {
    if (!reflection || busy) return;
    setBusy(true);
    const item = await api.publishedCase(reflection.recommendedTheme, {
      question,
      firstAnswer: firstChoice ? FIRST_CHOICES[firstChoice].title : "",
      secondAnswer: secondChoice ? STRUGGLE_CHOICES[secondChoice].title : "",
      thirdAnswer: dedication,
    });
    setJiaxingCase(item ?? (allowFixture ? DEV_CASE : null)); setCaseLoaded(true); setMobileCaseBeat(-1); setBusy(false); go("jiaxing", 320);
  }

  function createTicket(value: string) {
    const finalAction = cleanUserText(value);
    setTicket({
      action: finalAction,
      templateNo: TICKET_TEMPLATE_NO,
      issuedDate: todayLabel(),
      ticketCode: createTicketCode(),
    });
    go("ticket", 420);
  }

  async function submitAction() {
    const safe = cleanUserText(action);
    if (!safe) { showMessage("写下一件明天能做到的小事。"); return; }
    setAction(safe); setBusy(true);
    const result = await api.refineAction(sessionId, safe);
    setBusy(false);
    if (result.source !== "api") showMessage(AI_FALLBACK_NOTICE);
    if (cleanUserText(result.value.refined) === safe) { createTicket(safe); return; }
    setActionResult(result.value);
  }

  async function collectTicket() {
    if (!ticket || busy) return;
    setBusy(true);
    try {
      await downloadTicketPng(ticket, "red-boat-ticket.png", ticketArt);
      void api.track(sessionId, "ticket_generated");
      void api.complete({ sessionId, topic: analysis?.topic ?? "other", actionKeyword: actionKeywordFromText(ticket.action), caseId: jiaxingCase?.review_status === "published" ? jiaxingCase.id : null })
        .then((stats) => setCompleted(stats.value.completed));
    } catch {
      showMessage("浏览器没有完成图片保存，你仍然可以继续。");
    } finally {
      setBusy(false); go("lights", 520);
    }
  }

  function sceneIndex(value: V2Scene) {
    const index = sceneOrder.indexOf(value);
    const ticketIndex = sceneOrder.indexOf("ticket");
    if (index <= ticketIndex) {
      return `${String(index + 1).padStart(2, "0")} / ${String(ticketIndex + 1).padStart(2, "0")}`;
    }
    return value.toUpperCase();
  }

  const contextOne = analysis?.questionOne.context || "当熟悉的方法不再给出答案，前方也没有可以照着走的航标。";
  const contextTwo = questionTwo?.context || "你已经为这件事走了一段路，却还没有看见明显结果。";
  const activeCase = caseLoaded ? jiaxingCase : null;

  function renderScene(): ReactNode {
    if (scene === "intro") return (
      <section className="v2-scene v2-intro" data-testid="v2-intro">
        <p className="v2-place">嘉兴 · 南湖</p>
        <div className="v2-copy"><p className="v2-in">有些答案，</p>
          <h1 tabIndex={-1}><MaskLine at={2.35}>开始于一次<span className="v2-accent">选择</span>。</MaskLine></h1>
          <button className="v2-text-cta v2-in" type="button" onClick={() => go("question", 240)}>靠近一点 <span>→</span></button></div>
      </section>
    );

    if (scene === "question") return (
      <section className={"v2-scene v2-question" + (busy ? " is-sending" : "")} data-testid="v2-question">
        <div className="v2-copy">
          <h1 tabIndex={-1}><MaskLine at={.42}>如果今天站在这里，</MaskLine><MaskLine at={.58}>你最想问什么？</MaskLine></h1>
          <WaterInput className="v2-in" id="v2-question-input" label="不必想得太完整。" value={question} onChange={(event) => { setQuestion(event.target.value); typingRipple(); }} placeholder="用 5–20 个字，写下最近让你犹豫的事" autoFocus maxLength={20} />
          <button className="v2-thought-trigger v2-in" type="button" onClick={() => setShowQuestionThoughts((value) => !value)}>一时不知道怎么问？</button>
          <AnimatePresence>{showQuestionThoughts ? <motion.div className="v2-floating-lines" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {QUESTION_THOUGHTS.map((thought) => <button type="button" key={thought} onClick={() => setQuestion(thought)}>{thought}</button>)}
          </motion.div> : null}</AnimatePresence>
          <button className="v2-text-cta v2-in" type="button" disabled={busy} onClick={() => void submitQuestion()}>{busy ? "红船正在回应……" : "交给红船"} <span>→</span></button>
        </div>
        {busy ? <div className="v2-question-sending" aria-hidden="true"><i /><i /><i /><span>正在水面寻找你的问题……</span></div> : null}
        <div className="v2-sent-question" aria-hidden="true">{question}</div>
      </section>
    );

    if (scene === "first") return (
      <section className="v2-scene v2-first" data-testid="v2-first">
        <div className="v2-scene-kicker v2-chapter v2-in"><b>壹</b><span>首创</span><small>离岸 · 启程</small></div>
        <div className="v2-copy"><p className="v2-in">前面没有航标。</p>
          <h1 tabIndex={-1}><MaskLine at={.5}>你还会往<span className="v2-accent">前</span>吗？</MaskLine></h1>
          <small className="v2-in"><Typewriter text={contextOne} speed={30} startDelay={1900} /></small></div>
        <div className="v2-routes">
          <RouteText direction="left" mark="loop" {...FIRST_CHOICES.A} selected={firstChoice === "A"} disabled={Boolean(firstChoice)} onClick={() => void chooseFirst("A")} />
          <RouteText direction="right" mark="depart" {...FIRST_CHOICES.B} selected={firstChoice === "B"} disabled={Boolean(firstChoice)} onClick={() => void chooseFirst("B")} />
        </div>
      </section>
    );

    if (scene === "struggle") return (
      <section className="v2-scene v2-struggle" data-testid="v2-struggle">
        <div className="v2-scene-kicker v2-chapter v2-in"><b>贰</b><span>奋斗</span><small>湖心 · 行舟</small></div>
        <div className="v2-copy"><p className="v2-in">已经走了一段。</p>
          <h1 tabIndex={-1}><MaskLine at={.5}>可结果，</MaskLine><MaskLine at={.66}>还没有出现。</MaskLine></h1>
          <small className="v2-in"><Typewriter text={contextTwo} speed={30} startDelay={1900} /></small></div>
        <div className="v2-routes">
          <RouteText direction="left" mark="halt" {...STRUGGLE_CHOICES.A} selected={secondChoice === "A"} disabled={Boolean(secondChoice)} onClick={() => chooseStruggle("A")} />
          <RouteText direction="right" mark="surge" {...STRUGGLE_CHOICES.B} selected={secondChoice === "B"} disabled={Boolean(secondChoice)} onClick={() => chooseStruggle("B")} />
        </div>
      </section>
    );

    if (scene === "dedication") return (
      <section className={"v2-scene v2-dedication" + (pulse ? " is-leaving" : "")} data-testid="v2-dedication">
        <div className="v2-scene-kicker v2-chapter v2-in"><b>叁</b><span>奉献</span><small>穿雾 · 持灯</small></div>
        <div className="v2-copy"><p className="v2-in">如果最后，</p>
          <h1 tabIndex={-1}><MaskLine at={.48}><span className="v2-accent">没有人知道</span>这件事</MaskLine><MaskLine at={.64}>是你做的呢？</MaskLine></h1></div>
        <WaterInput className="v2-in" id="v2-dedication-input" label="我会……" value={dedication} onChange={(event) => { setDedication(event.target.value); typingRipple(); }} placeholder="把一句真实的话留在水面上" maxLength={80} />
        <div className={"v2-floating-lines" + (showDedicationThoughts ? " is-visible" : "")}>
          {DEDICATION_THOUGHTS.map((thought, index) => (
            <button type="button" key={thought} onClick={() => setDedication(thought)}>
              <Typewriter text={thought} speed={46} startDelay={showDedicationThoughts ? 320 + index * 260 : 0} />
            </button>
          ))}
        </div>
        <button className="v2-text-cta v2-in" type="button" disabled={!dedication.trim()} onClick={submitDedication}>留下这句话 <span>→</span></button>
        <div className="v2-answer-light" aria-hidden="true">{dedication}</div>
      </section>
    );

    if (scene === "reflection") {
      const recalls = {
        first: firstChoice ? FIRST_CHOICES[firstChoice].title + "，" + FIRST_CHOICES[firstChoice].detail : "你没有留下这一程的选择。",
        struggle: secondChoice ? STRUGGLE_CHOICES[secondChoice].title + "，" + STRUGGLE_CHOICES[secondChoice].detail : "你没有留下这一程的选择。",
        dedication: dedication || "你没有留下这一程的话。",
      };
      return (
        <section className="v2-scene v2-reflection" data-testid="v2-reflection">
          <div className="v2-copy"><p className="v2-in">走到这里，<br />再看看你最开始的问题。</p><blockquote>“{question}”</blockquote><h1 tabIndex={-1}>你真正反复问的，也许是——</h1></div>
          <div className="v2-reflection-words" role="status">
            {reflection ? splitReflection(reflection.reflection).map((paragraph, index) => (
              index <= reflectedCount ? (
                <p key={paragraph}>
                  <Typewriter
                    text={paragraph}
                    speed={26}
                    startDelay={index === 0 ? 280 : 0}
                    onDone={() => setReflectedCount((value) => Math.max(value, index + 1))}
                  />
                </p>
              ) : null
            )) : <p>水面正在安静下来……</p>}
          </div>
          <div className="v2-recall v2-in">
            {(["first", "struggle", "dedication"] as const).map((item) => <button type="button" key={item} onClick={() => setSelectedRecall(selectedRecall === item ? null : item)}>{item === "first" ? "首创" : item === "struggle" ? "奋斗" : "奉献"}</button>)}
            <AnimatePresence>{selectedRecall ? <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>{recalls[selectedRecall]}</motion.p> : null}</AnimatePresence>
          </div>
          <button className="v2-text-cta v2-in" type="button" disabled={!reflection || busy} onClick={() => void continueToTheory()}>看看红船精神的坐标 <span>→</span></button>
        </section>
      );
    }

    if (scene === "theory") {
      const visibleCards = theoryCards.filter((card) => card.review_status === "published" || card.source_review_status === "reviewed");
      const preferred = spiritThemeOf(reflection?.topic ?? analysis?.topic);
      const ordered = [...visibleCards].sort((a, b) => {
        const rank = (card: KnowledgeCard) => card.theme === preferred ? 0 : THEME_KEYS.indexOf(card.theme) + 1;
        return rank(a) - rank(b);
      });
      const activeCard = ordered.find((card) => card.theme === theoryChoice) ?? ordered[0] ?? null;
      const availableThemes = new Set(ordered.map((card) => card.theme));
      return (
        <section className="v2-scene v2-theory" data-testid="v2-theory">
          <div className="v2-copy"><p className="v2-in" style={{ "--d": ".25s" } as CssVars}>回看刚才的问题，</p>
            <h1 tabIndex={-1}><MaskLine at={.42}>红船精神给出了坐标。</MaskLine></h1>
            <small className="v2-in" style={{ "--d": ".95s" } as CssVars}><Typewriter text="首创 · 奋斗 · 奉献 —— 三句话，从南湖一直写到今天。" speed={34} startDelay={1250} /></small></div>
          {activeCard ? (
            <>
              <div className="v2-theory-card v2-in" key={activeCard.id} style={{ "--d": ".62s" } as CssVars}>
                <span className="v2-theory-kicker">{THEME_LABELS[activeCard.theme]} · 精神坐标</span>
                <h2>{activeCard.title}</h2>
                <div className="v2-theory-body">
                  {theoryParagraphs(activeCard.content).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                {activeCard.display_quote ? <blockquote className="v2-theory-quote">“{activeCard.display_quote}”</blockquote> : null}
              </div>
              <div className="v2-theory-switch">
                {THEME_KEYS.filter((key) => availableThemes.has(key)).map((key) => (
                  <button className="v2-in" type="button" key={key} aria-pressed={activeCard.theme === key} onClick={() => { audio.tap(); setTheoryChoice(key); }} style={{ "--d": "1.05s" } as CssVars}>{THEME_LABELS[key]}</button>
                ))}
              </div>
            </>
          ) : <p className="v2-theory-empty v2-in" style={{ "--d": ".7s" } as CssVars}>{theoryPending ? "正在取回这一段坐标……" : "这一页的内容还在核对，我们先往下走。"}</p>}
          <button className="v2-text-cta v2-in" type="button" disabled={theoryPending} onClick={() => void continueToJiaxing()}>看看嘉兴的答案 <span>→</span></button>
        </section>
      );
    }

    if (scene === "jiaxing") {
      const beats = activeCase ? storyBeats(activeCase) : null;
      const beatHeadings = ["他们遇到了什么？", "他们做了什么？", "后来发生了什么？"];
      return (
        <section className="v2-scene v2-jiaxing" data-testid="v2-jiaxing">
          <div className="v2-copy"><p className="v2-in">你的问题，</p><h1 tabIndex={-1}><MaskLine at={.38}>也有人在现实里回答。</MaskLine></h1><small className="v2-in">看看嘉兴的答案。</small></div>
          {beats && activeCase ? (
            <>
              <div className="v2-story-beats"><h2>{activeCase.title}</h2>
              {beatHeadings.map((heading, index) => (
                <div key={heading} style={{ animationDelay: (.6 + index * .9) + "s" }}><span>0{index + 1}</span><strong>{heading}</strong><p>{beats[index]}</p></div>
              ))}
              <p className="v2-source">{activeCase.source_url ? <a href={activeCase.source_url} target="_blank" rel="noreferrer">来源：{activeCase.source_name}</a> : activeCase.source_name}</p>
              </div>
              <div className="v2-mobile-case-flow">
                {mobileCaseBeat < 0 ? (
                  <button className="v2-case-entry" type="button" onClick={() => setMobileCaseBeat(0)}><i aria-hidden="true" />进入这个回答 <span>→</span></button>
                ) : (
                  <motion.article className="v2-mobile-story-beat" key={mobileCaseBeat} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                    <span>0{mobileCaseBeat + 1} / 03</span>
                    <h2>{beatHeadings[mobileCaseBeat]}</h2>
                    <p>{beats[mobileCaseBeat]}</p>
                    {mobileCaseBeat < 2 ? (
                      <button type="button" onClick={() => setMobileCaseBeat((value) => value + 1)}>继续读下去 <b>→</b></button>
                    ) : (
                      <><small className="v2-mobile-source">{activeCase.source_url ? <a href={activeCase.source_url} target="_blank" rel="noreferrer">来源：{activeCase.source_name}</a> : activeCase.source_name}</small><button type="button" onClick={() => go("action", 360)}>写下我的明天 <b>→</b></button></>
                    )}
                  </motion.article>
                )}
              </div>
            </>
          ) : <p className="v2-content-pending">正式案例正在等待来源审核。<br />本幕不展示未经发布的内容。</p>}
          <button className="v2-text-cta v2-in" type="button" onClick={() => go("action", 360)}>写下我的明天 <span>→</span></button>
        </section>
      );
    }

    if (scene === "action") return (
      <section className="v2-scene v2-action" data-testid="v2-action">
        <div className="v2-copy"><p className="v2-in">刚才，你看见了别人的答案。<br />现在，只剩最后一问。</p><h1 tabIndex={-1}><MaskLine at={.46}>明天，</MaskLine><MaskLine at={.62}>你准备先做什么？</MaskLine></h1></div>
        <WaterInput className="v2-in" id="v2-action-input" label="明天我会……" value={action} onChange={(event) => { setAction(event.target.value); setActionResult(null); typingRipple(); }} placeholder="写下一件能开始的小事" maxLength={80} />
        {actionResult ? (
          <div className="v2-refine"><p>要不要把这句话，<br />写得更像一次出发？</p>
            <strong>{actionResult.refined}</strong>
            <button className="v2-refine-use" type="button" onClick={() => createTicket(actionResult.refined)}>用这句话 <span>→</span></button>
            <button className="v2-refine-original" type="button" onClick={() => createTicket(actionResult.original)}>还是用我的原话</button>
          </div>
        ) : null}
        {!actionResult ? <button className="v2-text-cta v2-in" type="button" disabled={busy || !action.trim()} onClick={() => void submitAction()}>
          {busy ? "正在整理这句话……" : "写下这一步"} <span>→</span>
        </button> : null}
      </section>
    );

    if (scene === "ticket") return (
      <section className="v2-scene v2-ticket" data-testid="v2-ticket">
        <h1 className="v2-sr-only" tabIndex={-1}>红船青年船票 · 已启航</h1>
        {/* 票面本身渲染在背景层里（见下方 SceneBackground 的 children）——
            只有和背景图共享同一套变换，坐标才不会随缓推/漂移跑掉。 */}
        <div className="v2-ticket-message"><p>这不是标准答案。</p><strong>这是你的下一站。</strong></div>
        <button className="v2-text-cta" type="button" disabled={!ticket || busy} onClick={() => void collectTicket()}>{busy ? "正在收好船票……" : "收下船票"} <span>→</span></button>
      </section>
    );

    if (scene === "lights") return (
      <section className="v2-scene v2-lights" data-testid="v2-lights">
        {/* V1 里这幕承担了"公共光点墙"的说明义务：匿名完成数 + 只保留的行动关键词 +
            隐私声明。V2 重写时整段丢了，这里补回来（compliance 相关，不能只有 ember）。 */}
        <div className="v2-copy">
          <p className="v2-in">南湖水面。</p>
          <h1 tabIndex={-1}><MaskLine at={.4}>你的答案，已经留下一个光点。</MaskLine></h1>
          <small className="v2-in" style={{ "--d": ".9s" } as CssVars}>
            {ticket
              ? <>我们只保留一个抽象的行动关键词：<strong>{actionKeywordFromText(ticket.action)}</strong>。不公开你的问题，也不公开你的原话。</>
              : "这里只留下抽象的行动关键词，不公开任何人的问题与原话。"}
          </small>
        </div>
        {completed !== null && completed > 0 ? (
          <div className="v2-lights-count v2-in" style={{ "--d": "1.2s" } as CssVars}>
            <strong>{completed}</strong><span>位青年留下自己的答案</span>
          </div>
        ) : null}
        <div className="v2-light-boat" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /></div>
        <div className="v2-embers" aria-hidden="true">{EMBER_SPECS.map((spec, index) => <i className="v2-ember" key={index} style={spec} />)}</div>
        <button className="v2-text-cta v2-in" type="button" onClick={() => go("end", 360)}>继续 <span>→</span></button>
      </section>
    );

    return (
      <section className="v2-scene v2-end" data-testid="v2-end">
        <div className="v2-copy">
          <p className="v2-in" style={{ "--d": ".22s" } as CssVars}>一百多年前，<br />一条船从嘉兴出发。</p>
          <p className="v2-in" style={{ "--d": ".48s" } as CssVars}>今天，<br />新的答案仍在出发。</p>
          <h1 tabIndex={-1} aria-label="红船三问">{[...BRAND_TITLE].map((character, index) => <span className="v2-ch" key={index} style={{ "--i": index } as CssVars}>{character}</span>)}</h1></div>
        <div className="v2-ticket-message v2-in"><strong>AI时代的青年答案</strong></div>
        <div className="v2-seal-stamp v2-in" aria-hidden="true"><b>出</b><b>发</b></div>
        <button className="v2-text-cta v2-in" type="button" onClick={() => ticket ? go("ticket") : reset()}>查看我的船票 <span>→</span></button>
        <button className="v2-thought-trigger v2-in" type="button" onClick={reset}>再走一次</button>
      </section>
    );
  }

  return (
    <main className={"v2-experience v2-profile-" + profile + " v2-scene-" + scene} ref={mainRef}>
      <SceneBackground scene={scene} previousScene={previousScene} profile={profile} ticketArt={ticketArt}>
        {scene === "ticket" && ticket ? <TicketFace ticket={ticket} artKey={ticketArt} /> : null}
      </SceneBackground>
      <LivingWater scene={scene} profile={profile} pulse={pulse} />
      <EnvironmentOverlay scene={scene} pulse={pulse} />
      <header className="v2-hud">
        <button className="v2-brand" type="button" onClick={reset} aria-label="回到开场"><span>红船三问</span><small>AI时代的青年答案</small></button>
        <div className="v2-hud-index"><span><b>{sceneIndex(scene)}</b><em>{sceneLabel[scene]}</em></span><button type="button" onClick={() => { setSoundEnabled(!soundEnabled); audio.setEnabled(!soundEnabled); }}>{soundEnabled ? "声音 开" : "声音 关"}</button></div>
      </header>
      <div className="v2-chapter-rail" aria-hidden="true">
        {chapterMarks(scene).map((mark, index) => <span key={CHAPTER_LABELS[index]} className={"is-" + mark}><i /><em>{CHAPTER_LABELS[index]}</em></span>)}
      </div>
      <div className="v2-film" aria-hidden="true"><div className="v2-grain" /><div className="v2-vignette" /></div>
      <div className="v2-letterbox" aria-hidden="true"><i /><i /></div>
      <CursorGlow />
      <div className="v2-boot" aria-hidden="true">
        <span className="v2-boot-seal"><b>红</b><b>船</b><b>三</b><b>问</b></span>
        <em>JIAXING · SOUTH LAKE</em>
        <strong>嘉兴 · 南湖</strong>
        <i />
        <small>一九二一 —— 2026</small>
      </div>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div className="v2-scene-layer" key={scene}
          initial={{ opacity: 0, x: transitionDir * 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: transitionDir * -26 }}
          transition={{ opacity: { duration: reduceMotion ? .12 : .5 }, x: { duration: reduceMotion ? .12 : .64, ease: [0.22, 1, 0.36, 1] } }}>
          {renderScene()}
        </motion.div>
      </AnimatePresence>
      {notice ? <div className="v2-notice" role="status">{notice}</div> : null}
    </main>
  );
}
